import { FC, useState, useMemo, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import CheckAnimatedIcon from '@/components/ui/CheckAnimatedIcon';
import { formatDate } from '@/utils/formatters';

export type PeriodValue =
  | 'hoje'
  | 'esta-semana'
  | 'este-mes'
  | 'este-ano'
  | 'ultimos-30-dias'
  | 'ultimos-3-meses'
  | 'ultimos-6-meses'
  | 'ultimos-12-meses'
  | 'todo-periodo'
  | 'personalizado'
  | string;

export interface PeriodRange {
  inicio: string;
  fim: string;
}

interface PeriodOption {
  value: PeriodValue;
  label: string;
}

interface MesAno {
  mes: number;
  ano: number;
}

export interface PeriodFilterProps {
  value?: PeriodValue;
  onChange?: (period: PeriodValue, range: PeriodRange) => void;
  options?: PeriodValue[];
  dateField?: string;
  periodLabel?: string;
  showNavigation?: boolean;
  compact?: boolean;
  customPeriod?: PeriodRange;
  onCustomPeriodChange?: (period: PeriodRange) => void;
}

/**
 * PeriodFilter - Componente genérico de filtro de período
 *
 * @param {Object} props
 * @param {string} props.value - Período selecionado ('hoje', 'esta-semana', 'este-mes', etc.)
 * @param {Function} props.onChange - Callback (periodo, { inicio, fim }) => void
 * @param {string[]} props.options - Opções disponíveis (default: todas)
 * @param {string} props.dateField - Nome do campo de data para labels (ex: 'vencimento')
 * @param {string} props.periodLabel - Label customizado para o período (sobrescreve dateField)
 * @param {boolean} props.showNavigation - Mostrar setas de navegação (default: true)
 * @param {boolean} props.compact - Modo compacto para headers densos (default: false)
 * @param {Object} props.customPeriod - Período personalizado { inicio, fim }
 * @param {Function} props.onCustomPeriodChange - Callback para período personalizado
 */
const PeriodFilter: FC<PeriodFilterProps> = ({
  value = 'este-mes',
  onChange,
  options,
  dateField = 'data',
  periodLabel,
  showNavigation = true,
  compact = false,
  customPeriod = { inicio: '', fim: '' },
  onCustomPeriodChange
}) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showCustomModal, setShowCustomModal] = useState<boolean>(false);
  const [customTemp, setCustomTemp] = useState<PeriodRange>({ inicio: '', fim: '' });
  const [mesAnoAtual, setMesAnoAtual] = useState<MesAno>(() => {
    const hoje = new Date();
    return { mes: hoje.getMonth(), ano: hoje.getFullYear() };
  });
  const [periodoOffset, setPeriodoOffset] = useState<number>(0);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Opções padrão de período
  const defaultOptions: PeriodOption[] = [
    { value: 'hoje', label: 'Hoje' },
    { value: 'esta-semana', label: 'Esta semana' },
    { value: 'este-mes', label: 'Este mês' },
    { value: 'este-ano', label: 'Este ano' },
    { value: 'ultimos-30-dias', label: 'Últimos 30 dias' },
    { value: 'ultimos-3-meses', label: 'Últimos 3 meses' },
    { value: 'ultimos-6-meses', label: 'Últimos 6 meses' },
    { value: 'ultimos-12-meses', label: 'Últimos 12 meses' },
    { value: 'todo-periodo', label: 'Todo o período' },
    { value: 'personalizado', label: 'Período personalizado' }
  ];

  const availableOptions = useMemo<PeriodOption[]>(() => {
    if (!options) return defaultOptions;
    return defaultOptions.filter(opt => options.includes(opt.value));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (dropdownRef.current && target && !dropdownRef.current.contains(target)) {
        setShowDropdown(false);
      }
      if (modalRef.current && target && !modalRef.current.contains(target)) {
        setShowCustomModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calcular período baseado na seleção
  // IMPORTANTE: As datas incluem hora para funcionar corretamente com campos timestamp
  // inicio = 00:00:00 (início do dia), fim = 23:59:59.999 (fim do dia)
  const calcularPeriodo = (periodo: PeriodValue, mesAno: MesAno = mesAnoAtual, offset: number = periodoOffset): PeriodRange => {
    const hoje = new Date();
    let inicio: string;
    let fim: string;

    // Helper para formatar data com hora de início e fim
    const formatInicio = (date: Date): string => date.toISOString().split('T')[0] + 'T00:00:00';
    const formatFim = (date: Date): string => date.toISOString().split('T')[0] + 'T23:59:59.999';

    switch (periodo) {
      case 'hoje': {
        const data = new Date(hoje);
        data.setDate(data.getDate() + offset);
        inicio = formatInicio(data);
        fim = formatFim(data);
        break;
      }
      case 'esta-semana': {
        const primeiroDia = new Date(hoje);
        primeiroDia.setDate(hoje.getDate() - hoje.getDay() + (offset * 7));
        const ultimoDia = new Date(primeiroDia);
        ultimoDia.setDate(primeiroDia.getDate() + 6);
        inicio = formatInicio(primeiroDia);
        fim = formatFim(ultimoDia);
        break;
      }
      case 'este-mes':
        inicio = formatInicio(new Date(mesAno.ano, mesAno.mes, 1));
        fim = formatFim(new Date(mesAno.ano, mesAno.mes + 1, 0));
        break;
      case 'este-ano': {
        inicio = formatInicio(new Date(mesAno.ano, 0, 1));
        fim = formatFim(new Date(mesAno.ano, 11, 31));
        break;
      }
      case 'ultimos-30-dias': {
        const inicio30 = new Date(hoje);
        inicio30.setDate(hoje.getDate() - 30 + (offset * 30));
        const fim30 = new Date(inicio30);
        fim30.setDate(inicio30.getDate() + 30);
        inicio = formatInicio(inicio30);
        fim = formatFim(fim30);
        break;
      }
      case 'ultimos-3-meses': {
        const inicio3 = new Date(hoje);
        inicio3.setMonth(hoje.getMonth() - 3 + (offset * 3));
        const fim3 = new Date(inicio3);
        fim3.setMonth(inicio3.getMonth() + 3);
        inicio = formatInicio(inicio3);
        fim = formatFim(fim3);
        break;
      }
      case 'ultimos-6-meses': {
        const inicio6 = new Date(hoje);
        inicio6.setMonth(hoje.getMonth() - 6 + (offset * 6));
        const fim6 = new Date(inicio6);
        fim6.setMonth(inicio6.getMonth() + 6);
        inicio = formatInicio(inicio6);
        fim = formatFim(fim6);
        break;
      }
      case 'ultimos-12-meses': {
        const inicio12 = new Date(hoje);
        inicio12.setMonth(hoje.getMonth() - 12 + (offset * 12));
        const fim12 = new Date(inicio12);
        fim12.setMonth(inicio12.getMonth() + 12);
        inicio = formatInicio(inicio12);
        fim = formatFim(fim12);
        break;
      }
      case 'personalizado':
        // Para período personalizado, adicionar hora se não tiver
        inicio = customPeriod.inicio
          ? (customPeriod.inicio.includes('T') ? customPeriod.inicio : customPeriod.inicio + 'T00:00:00')
          : formatInicio(hoje);
        fim = customPeriod.fim
          ? (customPeriod.fim.includes('T') ? customPeriod.fim : customPeriod.fim + 'T23:59:59.999')
          : formatFim(hoje);
        break;
      case 'todo-periodo':
      default:
        inicio = '2000-01-01T00:00:00';
        fim = '2099-12-31T23:59:59.999';
    }

    return { inicio, fim };
  };

  // Gerar label para exibição
  const getPeriodoLabel = (): string => {
    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const mesesAbrev = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
                        'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    switch (value) {
      case 'hoje': {
        const data = new Date();
        data.setDate(data.getDate() + periodoOffset);
        return formatDate(data);
      }
      case 'esta-semana': {
        const { inicio, fim } = calcularPeriodo('esta-semana');
        const inicioDate = new Date(inicio);
        const fimDate = new Date(fim);
        if (compact) {
          return `${inicioDate.getDate()}/${inicioDate.getMonth() + 1} - ${fimDate.getDate()}/${fimDate.getMonth() + 1}`;
        }
        return `${formatDate(inicioDate)} - ${formatDate(fimDate)}`;
      }
      case 'este-mes':
        if (compact) {
          return `${mesesAbrev[mesAnoAtual.mes]}/${String(mesAnoAtual.ano).slice(-2)}`;
        }
        return `${meses[mesAnoAtual.mes]} ${mesAnoAtual.ano}`;
      case 'este-ano':
        return `${mesAnoAtual.ano}`;
      case 'ultimos-30-dias':
        return compact ? '30 dias' : 'Últimos 30 dias';
      case 'ultimos-3-meses':
        return compact ? '3 meses' : 'Últimos 3 meses';
      case 'ultimos-6-meses':
        return compact ? '6 meses' : 'Últimos 6 meses';
      case 'ultimos-12-meses':
        return compact ? '12 meses' : 'Últimos 12 meses';
      case 'todo-periodo':
        return compact ? 'Todos' : 'Todo o período';
      case 'personalizado':
        if (customPeriod.inicio && customPeriod.fim) {
          const inicioDate = new Date(customPeriod.inicio + 'T12:00:00');
          const fimDate = new Date(customPeriod.fim + 'T12:00:00');
          if (compact) {
            return `${inicioDate.getDate()}/${inicioDate.getMonth() + 1} - ${fimDate.getDate()}/${fimDate.getMonth() + 1}`;
          }
          return `${formatDate(inicioDate)} - ${formatDate(fimDate)}`;
        }
        return compact ? 'Custom' : 'Período personalizado';
      default:
        return value;
    }
  };

  // Navegar para período anterior/posterior
  const navegarPeriodo = (direcao: number): void => {
    if (value === 'todo-periodo' || value === 'personalizado') return;

    if (value === 'este-mes') {
      setMesAnoAtual(prev => {
        const novoMes = prev.mes + direcao;
        if (novoMes > 11) {
          return { mes: 0, ano: prev.ano + 1 };
        } else if (novoMes < 0) {
          return { mes: 11, ano: prev.ano - 1 };
        }
        return { ...prev, mes: novoMes };
      });
    } else if (value === 'este-ano') {
      setMesAnoAtual(prev => ({ ...prev, ano: prev.ano + direcao }));
    } else {
      setPeriodoOffset(prev => prev + direcao);
    }
  };

  // Emitir mudança de período quando navegação (setas) é usada
  // NÃO executar na mudança de value - isso é feito no handleSelectPeriodo
  useEffect(() => {
    // Este efeito só deve executar para atualizar quando navegar com as setas
    // A mudança inicial de período é tratada em handleSelectPeriodo
    if (onChange && (value === 'este-mes' || value === 'este-ano')) {
      const datas = calcularPeriodo(value, mesAnoAtual, periodoOffset);
      onChange(value, datas);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesAnoAtual]);

  // Efeito separado para períodos que usam offset
  useEffect(() => {
    if (onChange && value !== 'este-mes' && value !== 'este-ano' && value !== 'personalizado' && value !== 'todo-periodo') {
      const datas = calcularPeriodo(value, mesAnoAtual, periodoOffset);
      onChange(value, datas);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoOffset]);

  // Selecionar período do dropdown
  const handleSelectPeriodo = (opcao: PeriodValue): void => {
    setPeriodoOffset(0);
    setShowDropdown(false);

    if (opcao === 'personalizado') {
      setCustomTemp({ ...customPeriod });
      setShowCustomModal(true);
    } else {
      // Para 'este-mes' e 'este-ano', resetar para mês/ano atual
      let mesAnoParaCalculo = mesAnoAtual;
      if (opcao === 'este-mes' || opcao === 'este-ano') {
        const hoje = new Date();
        mesAnoParaCalculo = { mes: hoje.getMonth(), ano: hoje.getFullYear() };
        setMesAnoAtual(mesAnoParaCalculo);
      }

      if (onChange) {
        // Calcular datas com offset 0 e mesAno atualizado
        const datas = calcularPeriodo(opcao, mesAnoParaCalculo, 0);
        onChange(opcao, datas);
      }
    }
  };

  // Aplicar período personalizado
  const aplicarPeriodoCustom = (): void => {
    if (customTemp.inicio && customTemp.fim) {
      if (onCustomPeriodChange) {
        onCustomPeriodChange(customTemp);
      }
      if (onChange) {
        onChange('personalizado', customTemp);
      }
      setShowCustomModal(false);
    }
  };

  const canNavigate = value !== 'todo-periodo' && value !== 'personalizado';

  return (
    <div style={{ position: 'relative', overflow: 'visible' }}>
      {/* Label - oculto no modo compact */}
      {!compact && (
        <div className="period-filter__label">
          {periodLabel || `Período de ${dateField}`}
        </div>
      )}

      <div className="period-filter">
        {/* Botão Anterior */}
        {showNavigation && (
          <button
            className={`period-filter__nav ${compact ? 'period-filter__nav--compact period-filter__nav--left' : ''}`}
            onClick={() => navegarPeriodo(-1)}
            disabled={!canNavigate}
          >
            <ChevronLeft size={compact ? 14 : 16} />
          </button>
        )}

        {/* Botão Principal / Dropdown */}
        <div ref={dropdownRef} className={`period-filter__selector-wrapper ${compact ? 'period-filter__selector-wrapper--compact' : ''}`}>
          <button
            className={`period-filter__selector ${compact ? 'period-filter__selector--compact' : ''}`}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <span className="period-filter__selector-text">{getPeriodoLabel()}</span>
            <ChevronDown size={compact ? 12 : 16} className="period-filter__selector-icon" />
          </button>

          {/* Dropdown de opções */}
          {showDropdown && (
            <div className="period-filter__dropdown">
              {availableOptions.map((opcao) => (
                <div
                  key={opcao.value}
                  className={`period-filter__option ${value === opcao.value ? 'period-filter__option--selected' : ''}`}
                  onClick={() => handleSelectPeriodo(opcao.value)}
                >
                  {opcao.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botão Próximo */}
        {showNavigation && (
          <button
            className={`period-filter__nav ${compact ? 'period-filter__nav--compact period-filter__nav--right' : ''}`}
            onClick={() => navegarPeriodo(1)}
            disabled={!canNavigate}
          >
            <ChevronRight size={compact ? 14 : 16} />
          </button>
        )}
      </div>

      {/* Modal de Período Personalizado */}
      {showCustomModal && (
        <div
          ref={modalRef}
          className="period-filter__custom-modal"
        >
          {/* Seta apontando para cima */}
          <div className="period-filter__custom-modal-arrow" />

          {/* Campos de data */}
          <div className="period-filter__custom-fields">
            <div className="period-filter__custom-field">
              <label className="period-filter__custom-label">
                Data inicial <span className="required">*</span>
              </label>
              <input
                type="date"
                className="ac-input ac-input--sm"
                value={customTemp.inicio}
                onChange={(e) => setCustomTemp(prev => ({ ...prev, inicio: e.target.value }))}
              />
            </div>

            <div className="period-filter__custom-separator">
              até
            </div>

            <div className="period-filter__custom-field">
              <label className="period-filter__custom-label">
                Data final <span className="required">*</span>
              </label>
              <input
                type="date"
                className="ac-input ac-input--sm"
                value={customTemp.fim}
                onChange={(e) => setCustomTemp(prev => ({ ...prev, fim: e.target.value }))}
              />
            </div>
          </div>

          {/* Botões */}
          <div className="period-filter__custom-actions">
            <button
              className="btn-add undo-animated"
              onClick={() => setShowCustomModal(false)}
            >
              <UndoAnimatedIcon size={15} /> <span className="btn-text">Cancelar</span>
            </button>
            <button
              className="btn-add checkmark-animated"
              onClick={aplicarPeriodoCustom}
              disabled={!customTemp.inicio || !customTemp.fim}
            >
              <CheckAnimatedIcon size={14} /> <span className="btn-text">Aplicar filtro</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodFilter;
